import CustomImage from '@/components/CustomImage/CustomImage.jsx';
import { getProductInfo, selectItem, viewItem, aislesViewAnalyticsFunction } from '@/utils/analytics.js';
import { enhanceProductWithSlug } from '@/utils/products/productSlugs.js';
import Link from 'next/link';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import cn from 'classnames';
import { getBrandProducts, getProductDetailsV3, getProductInfoV3, getProductOfferDuration } from '@/api/Products';
import PageLayout from '@/components/PageLayout';
import useWindowUnderThreshold from '@/hooks/useWindowUnderThreshold';
import { getCategoryDetailsFromSlug } from '@/utils/category';
import {
  getProductAlternativeImages,
  transformProduct,
  // addPrimaryImage,
  addProduct,
  removeProduct,
  openListSideSheet,
  initMyoProducts,
  createStringArray,
  extractCommentNonOption,
  getProductPropsWithCleanUrl,
  getProductBasedOnSlug,
  getProductIndexBasedOnSlug,
  updateSharedCartWithComments,
  checkIfInCart,
} from '@/utils/products';
import Head from 'next/head';
import styles from '@/styles/Categories.module.css';
import productStyles from '@/styles/Products.module.css';
import { getLayoutProps } from '@/utils/layout/layout';
import useBundleOffersSummary from '@/hooks/useBundleOffersSummary';
import { BundleOfferSideSheetStore } from '@/store/sideSheet';
import { SelectedBundleOfferStore } from '@/store/selectedBundleOfferId';
import { checkListStatus } from '@/utils/lists';
import { productsForOrder } from '@/store/cart';
import { getOrFetchConfiguration } from '@/utils/getConfiguration';
import {
  ProductDetailsImageSection,
  ProductCustomization,
  ProductInfo,
  NutritionFacts,
  ProductVideo,
  ProductContentGroup,
  SnackBar,
  ProductAisleV2,
  WaveBreadCrumb,
} from '@desquared/wave-components-library';
import { DeliveryServicesContext } from '@/contexts/DeliveryServicesContext';
import { getFrequentlyBoughtTogether, getSimilarProducts } from '@/api/lists';
import { fetchCart } from '@/api/syncCart';
import useSimilarAndFrequentProducts from '@/hooks/useSimilarAndFrequentProducts';
import useProductTags from '@/hooks/useProductTags';
import { useRouter } from 'next/router';
import { useTranslations } from 'next-intl';
import UserListsStore from '@/store/userLists';
import { CurrencyContext } from '@/contexts/CurrencyProvider';
import DefaultProductImage from '../../../public/assets/wave-grocery/Empty-States-Default/DefaultProductImage.png';
import {
  createAvailabilityTextDetails,
  describeDeliveryRule,
  createTimeFrameAvailabilityTextDetails,
} from '@/utils/products/products';
import isEmpty from 'lodash/isEmpty';
import Cookies from 'universal-cookie';
import { getProductPriceHistory } from '@/api/Products';
import dayjs from 'dayjs';
import { useConfiguration } from '@/contexts/ConfigContext';
import { useQuery } from '@tanstack/react-query';
import { isEqual } from 'lodash';
import { arraysEqual } from '@/utils/helpers';
import { useSessionId } from '@/hooks/useSessionId';
import * as sanitizeHtml from 'sanitize-html';
import ListStore from '@/store/productItemList';
import { usePersonalizedProductsIds } from '@/contexts/PersonalizedProductsIdsContext.jsx';
import useBrandProducts from '@/hooks/useBrandProducts.js';
import useDeliveryServices from '@/hooks/useDeliveryServices';
import { titleCaseString } from '@/utils/stringManipulationUtils.js';
import { useIsRtl } from '@/contexts/DirectionContext.jsx';

export default function Products({
  responsiveLogo,
  productInfoInitial,
  productDetails,
  selectedCategory,
  similarProducts,
  frequentlyBoughtTogether,
  brandProductsApiRes,
  emptyStates,
  productId,
}) {
  const { config: configuration } = useConfiguration();
  const [isDateOfferEnabled, setIsDateOfferEnabled] = useState(false);
  const [hasDateOfferPriceApplied, setHasDateOfferPriceApplied] = useState(false);
  const [formattedProductOfferDuration, setFormattedProductOfferDuration] = useState({
    label: '',
    startDate: '',
    endDate: '',
  });

  const router = useRouter();
  const { data: productInfo, isFetching: productInfoIsLoading } = useQuery({
    queryKey: ['product', productInfoInitial._id, router.locale],
    queryFn: () => getProductInfoV3(productInfoInitial._id, router.locale, process.env.PRIVATE_API_KEY),
    initialData: productInfoInitial,
    refetchOnMount: 'always',
  });

  useEffect(() => {
    setIsDateOfferEnabled(configuration?.catalog?.product?.dateOfferPrices?.enabled);
    setHasDateOfferPriceApplied(productInfo?.hasDateOfferPriceApplied || false);
  }, [configuration?.catalog?.product?.dateOfferPrices?.enabled, configuration, productInfo, isDateOfferEnabled]);

  const isQueryEnabled = isDateOfferEnabled && hasDateOfferPriceApplied;

  const { data: productOfferDuration } = useQuery({
    queryKey: ['product-offer-duration', productInfoInitial._id, router.locale],
    queryFn: () => getProductOfferDuration(productInfoInitial._id),
    enabled: isQueryEnabled,
    select: res => res,
  });

  useEffect(() => {
    if (!isQueryEnabled) {
      setFormattedProductOfferDuration(undefined);
      return;
    }
    if (!productOfferDuration) {
      setFormattedProductOfferDuration(undefined);
      return;
    }
    const formattedStartDate = dayjs(productOfferDuration?.startsAt).format('D MMM');
    const formattedEndDate = dayjs(productOfferDuration?.endsAt).format('D MMM');
    setFormattedProductOfferDuration({
      label: productOfferDuration?.label,
      startDate: formattedStartDate,
      endDate: formattedEndDate,
    });
  }, [productOfferDuration, isDateOfferEnabled, router.locale, isQueryEnabled]);

  const cookies = new Cookies();
  const authorization = cookies.get('token');
  const t = useTranslations();
  const a = useTranslations('common.asides');
  const [textAreaValue, setTextAreaValue] = useState('');
  const { productTags } = useProductTags();
  const { bundleOffersSummary } = useBundleOffersSummary();
  const { frequent, similar } = useSimilarAndFrequentProducts(frequentlyBoughtTogether, similarProducts, a);
  const [hasBundleOffer, setHasBundleOffer] = useState(false);
  const [showSnackBar, setShowSnackBar] = useState(false);
  const { brandProducts } = useBrandProducts(brandProductsApiRes, a, setShowSnackBar);
  const [bundleDescription, setBundleDescription] = useState('');
  const isMobile = useWindowUnderThreshold(850);
  const { locale, defaultLocale, back, query, push, asPath } = useRouter();
  const [optionsGroups, setOptionsGroups] = useState([]);
  const [comments, setComments] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [createdProducts, setCreatedProducts] = useState([]);
  const [productsOrder, setProductsOrder] = useState();
  const { personalizedProductsIds } = usePersonalizedProductsIds();
  const isRtl = useIsRtl();

  const lightBoxAriaLabels = {
    closeAriaLabel: t('common.asides.side_cart.close'),
    previousSlideAriaLabel: t('common.previous_slide_aria_label'),
    nextSlideAriaLabel: t('common.next_slide_aria_label'),
    carouselAriaLabel: t('common.carousel_aria_label'),
  };

  const [productFormatted, setProductFormatted] = useState({
    ...productInfo,
    ...transformProduct(
      productInfo,
      null,
      undefined,
      productTags,
      bundleOffersSummary,
      undefined,
      undefined,
      query?.preSelectedIngredients ? JSON.parse(query?.preSelectedIngredients) : undefined,
      personalizedProductsIds,
      a
    ),
  });
  const [slideIndex, setSlideIndex] = useState();
  const [toastMessage, setToastMessage] = useState('');
  const [lists, setLists] = useState([]);
  const [config, setConfig] = useState();
  const [showBarcode, setShowBarcode] = useState(false);
  const { formatter } = useContext(CurrencyContext);
  const { selectedUserAddressIndex } = useContext(DeliveryServicesContext);
  const [commentMaxLength, setCommentMaxLength] = useState(100);
  const [hasCartAdditionText, setHasCartAdditionText] = useState(false);
  const defaultProdImg = emptyStates?.default_product_image?.url || DefaultProductImage.src;
  const { shop, getCollectionTypes, selectedService } = useDeliveryServices();

  const [shouldDisplayPriceHistory, setShouldDisplayPriceHistory] = useState(false);
  const [ingredientDefaultValues, setIngredientDefaultValues] = useState(undefined);
  const isBrandPageEnabled = configuration?.catalog?.brand?.specialPageDisplay?.enabled;
  const sessionId = useSessionId();
  const [product, setProduct] = useState({
    id: productFormatted._id,
    comments:
      productFormatted?.optionsGroups?.length > 0
        ? comments[0] === t('common.asides.side_cart.no_comment')
          ? ''
          : comments[0]
        : productFormatted.comments === t('common.asides.side_cart.no_comment')
        ? ''
        : productFormatted.comments,
    ...productFormatted,
    oldPrice:
      productFormatted?.optionsGroups?.length > 0
        ? productFormatted.optionsGroups[0].options[selectedIndex].association?.beginPricePerStepToUom ===
          productFormatted.optionsGroups[0].options[selectedIndex].association?.finalPricePerStepToUom
          ? productFormatted.optionsGroups[0].options[selectedIndex].association?.beginPricePerStepToUom
          : undefined
        : productFormatted.finalPrice !== productFormatted.beginPrice
        ? ((productFormatted.beginPrice * productFormatted.stepToUom) / 100).toFixed(2)
        : undefined,
  });

  const { data: packagedProduct } = useQuery({
    queryKey: ['packagedProduct', productInfo?.packagingProductId, locale],
    queryFn: ({ queryKey }) => getProductInfoV3(productInfo?.packagingProductId, locale, process.env.PRIVATE_API_KEY),
    staleTime: 1000 * 60 * 30,
    enabled: !!productInfo?.packagingProductId && !!config?.catalog?.product?.packagingFee?.enabled,
  });

  useEffect(() => {
    if (configuration?.catalog?.product?.details?.showPriceHistory) setShouldDisplayPriceHistory(true);
  }, [configuration, setShouldDisplayPriceHistory]);

  useEffect(() => {
    async function getConfiguration() {
      const configuration = await getOrFetchConfiguration();
      setConfig(configuration);
      setShowBarcode(configuration?.catalog?.product?.details?.showBarcode ?? false);
      setCommentMaxLength(configuration?.fields?.orderItems?.comments?.maxLength ?? 100);
      return;
    }

    getConfiguration();
  }, []);

  // useEffect(() => {
  //   const fetchCartData = async () => {
  //     const collectionTypes = getCollectionTypes();
  //     if (!collectionTypes) return;
  //     if (authorization != null) {
  //       const result = await fetchCart(); // Wait for API response
  //       const validProducts = result?.data?.products?.filter(item => item?.productId) ?? []; // Filter out invalid items
  //       if (validProducts?.length > 0) {
  //         transformCart(
  //           validProducts,
  //           'none',
  //           collectionTypes,
  //           selectedUserAddressIndex,
  //           bundleOffersSummary,
  //           t('common.asides.side_cart.no_comment'),
  //           undefined,
  //           undefined,
  //           undefined,
  //           formatter,
  //           undefined,
  //           a
  //         );
  //       }
  //     }
  //   };
  //   if (!isEmpty(authorization) && process.env.NEXT_PUBLIC_SYNC_CART === 'true') {
  //     fetchCartData();
  //   }
  // }, []);

  const unwrapIngredientsArray = ingredientsArray => {
    if (!productInfo.ingredientsGroups) return {};

    let initialValues = {};
    for (const group of productInfo.ingredientsGroups) {
      const isExclusive = group.maxOptionsAllowed === 1 && group.minOptionsAllowed === 1;

      for (const option of group.options) {
        if (ingredientsArray.includes(option.id) && option.available) {
          if (Object.hasOwn(initialValues, group.id)) initialValues[group.id].push(option.id);
          else {
            initialValues[group.id] = isExclusive ? option.id : [option.id];
          }
        }
      }

      if (!(group.id in initialValues)) {
        initialValues[group.id] = isExclusive ? '' : [];
      }
    }

    return initialValues;
  };

  const flattenIngredientsArray = ingredientsValues => {
    const ingredients = [];
    for (const key in ingredientsValues) {
      for (const ingredient of ingredientsValues[key]) if (ingredient) ingredients.push(ingredient);
    }

    return ingredients;
  };

  useEffect(() => {
    const { preSelectedIngredients } = query;
    if (!preSelectedIngredients) {
      setIngredientDefaultValues(undefined);
      return;
    }

    try {
      const parsedIngredients = JSON.parse(decodeURIComponent(preSelectedIngredients));
      setIngredientDefaultValues(unwrapIngredientsArray(parsedIngredients));
    } catch (error) {
      console.error('Failed to parse preSelectedIngredients:', error);
    }
  }, [query]);

  const ingredientsUpdateCurrent = ingredients => {
    let count = 0;
    const defaultIngredientArray = [];

    for (const key in ingredientDefaultValues) {
      for (const ingredient of ingredientDefaultValues[key]) {
        defaultIngredientArray.push(ingredient);
      }
    }

    while (checkIfInCart({ ...productFormatted, ingredients: defaultIngredientArray })) {
      handleProductChange(product, 'remove', ingredientDefaultValues);
      count++;
    }

    const newDefaultIngredientArray = [];

    for (const key in ingredients) {
      for (const ingredient of ingredients[key]) {
        newDefaultIngredientArray.push(ingredient);
      }
    }

    for (let i = 0; i < count; i++) {
      handleProductChange(product, 'add', ingredients);
    }

    push(
      {
        pathname: asPath.split('?')[0],
        search: `?preSelectedIngredients=${JSON.stringify(newDefaultIngredientArray)}`,
      },
      undefined,
      {
        shallow: true,
      }
    );
  };

  const ingredientsAddAsNew = ingredients => {
    handleProductChange(product, 'add', ingredients);
    const defaultIngredientArray = [];

    for (const key in ingredients) {
      for (const ingredient of ingredients[key]) {
        defaultIngredientArray.push(ingredient);
      }
    }

    push(
      {
        pathname: asPath.split('?')[0],
        search: `?preSelectedIngredients=${JSON.stringify(defaultIngredientArray)}`,
      },
      undefined,
      {
        shallow: true,
      }
    );
  };

  const handleProductChange = (product, action, ingredients = null, quantity) => {
    let productWithIngredients;
    if (ingredients) {
      let ingredientIds = [];
      if (!Array.isArray(ingredients)) {
        for (const key in ingredients) {
          for (const id of ingredients[key]) ingredientIds.push(id);
        }
      } else {
        ingredientIds = ingredients;
      }

      productWithIngredients = { ...productFormatted, ingredients: ingredientIds };
    }

    if (action === 'add') {
      if (productFormatted.optionsGroups.length > 0) {
        addProduct(
          createdProducts[selectedIndex],
          true,
          ListStore.getProductId() === productFormatted._id
            ? {
                item_list_id: ListStore.getListName(),
                item_list_name: ListStore.getListName(),
                session_id: sessionId,
              }
            : {
                item_list_id: 'product_details',
                item_list_name: product?.displayName || '',
              },
          false,
          t('common.asides.side_cart.no_comment'),
          false,
          quantity
        );
        setCreatedProducts(initMyoProducts(productFormatted, comments));
        getOptions(productFormatted?.optionsGroups);
        setToastMessage(createStringArray(optionsGroups[0].options, createdProducts, t));
      } else {
        addProduct(
          {
            id: productFormatted._id,
            ...(productWithIngredients ? productWithIngredients : productFormatted),
            comments: comments[0] === t('common.asides.side_cart.no_comment') ? '' : comments[0],
          },
          true,
          ListStore.getProductId() === productFormatted._id
            ? {
                item_list_id: ListStore.getListName(),
                item_list_name: ListStore.getListName(),
                session_id: sessionId,
              }
            : { item_list_id: 'product_details', item_list_name: product?.displayName || '' },
          false,
          t('common.asides.side_cart.no_comment'),
          !!productWithIngredients,
          quantity
        );
        setProductFormatted({
          ...productInfo,
          ...transformProduct(
            productInfo,
            null,
            undefined,
            productTags,
            bundleOffersSummary,
            undefined,
            undefined,
            ingredients,
            personalizedProductsIds,
            a
          ),
        });
      }
      if (process.env.hasItemAdditionMessage) {
        setHasCartAdditionText(true);
      }
    } else if (action === 'remove') {
      if (productFormatted.optionsGroups.length > 0) {
        removeProduct(
          createdProducts[selectedIndex],
          {
            item_list_id: 'product_details',
            item_list_name: product?.displayName || '',
          },
          undefined,
          false,
          quantity
        );
        setCreatedProducts(initMyoProducts(productFormatted, undefined));
        getOptions(productFormatted?.optionsGroups);
        if (process.env.hasItemAdditionMessage) {
          setHasCartAdditionText(removeAddText(productFormatted));
        }
      } else {
        removeProduct(
          { ...(productWithIngredients ? productWithIngredients : productFormatted) },
          { item_list_id: 'product_details', item_list_name: product?.displayName || '' },
          undefined,
          !!productWithIngredients,
          quantity
        );
        setProductFormatted({
          ...productInfo,
          ...transformProduct(
            productInfo,
            null,
            undefined,
            productTags,
            bundleOffersSummary,
            undefined,
            undefined,
            ingredients,
            personalizedProductsIds,
            a
          ),
        });
        if (process.env.hasItemAdditionMessage) {
          setHasCartAdditionText(removeAddText(product));
        }
      }
    }

    setProduct({
      id: productInfo._id,
      ...productInfo,
      ...transformProduct(
        productInfo,
        null,
        undefined,
        productTags,
        bundleOffersSummary,
        undefined,
        undefined,
        ingredients,
        personalizedProductsIds,
        a
      ),
      comments:
        productFormatted?.optionsGroups?.length > 0
          ? comments[0] === t('common.asides.side_cart.no_comment')
            ? ''
            : comments[0]
          : productFormatted.comments === t('common.asides.side_cart.no_comment')
          ? ''
          : productFormatted.comments,
      oldPrice:
        productFormatted.optionsGroups.length > 0
          ? productFormatted.optionsGroups[0].options[selectedIndex].association?.beginPricePerStepToUom ===
            productFormatted.optionsGroups[0].options[selectedIndex].association?.finalPricePerStepToUom
            ? productFormatted.optionsGroups[0].options[selectedIndex].association?.beginPricePerStepToUom
            : undefined
          : productFormatted.finalPrice !== productFormatted.beginPrice
          ? ((productFormatted.beginPrice * productFormatted.stepToUom) / 100).toFixed(2)
          : undefined,
    });
  };
  const getOptions = groups => {
    if (groups.length > 0) {
      const firstGroup = groups[0];

      const groupOptions = firstGroup.options.map((option, index) => {
        return {
          text: option.label,
          selected: selectedOptions[firstGroup.id] === option.id || index === selectedIndex,
          isOpted: createdProducts[index]?.modifiedUom > 0,
        };
      });

      const optionsData = [
        {
          title: firstGroup.label,
          options: groupOptions,
        },
      ];

      setOptionsGroups(optionsData);

      const defaultSelectedOption = groupOptions[0]?.text || '';
      setSelectedOptions({ [firstGroup.id]: defaultSelectedOption });
      if (optionsData[0].options.length > 0) {
        setComments(optionsData[0]?.options?.map(el => ''));
        setToastMessage(createStringArray(optionsData[0]?.options, createdProducts, t));
      }
    }
  };
  const handleCommentChange = comment2 => {
    if (comment2 === undefined) return;
    setComments(prevComments => {
      const updatedComments = [...prevComments];
      updatedComments[selectedIndex] = comment2;
      return updatedComments;
    });

    let entries = productsForOrder.getValue();

    if (productFormatted.optionsGroups?.length > 0) {
      entries.forEach(entry => {
        if (entry.myoId === createdProducts[selectedIndex]?.myoId && createdProducts[selectedIndex]?.myoId) {
          entry.comments = comment2;
          entry.description = comment2;
        }
      });
    } else if (productFormatted.ingredientsGroups?.length > 0) {
      const ingredientsArray = flattenIngredientsArray(ingredientDefaultValues || {});
      entries.forEach(entry => {
        if (entry.id === product.id && entry.ingredients && arraysEqual(ingredientsArray, entry.ingredients)) {
          entry.comments = comment2;
          entry.description = comment2;
        }
      });
    } else {
      if (comment2[0] === '' || comment2.length === 0 || comment2 === '') return;
      entries.forEach(entry => {
        if (entry.id === productId) {
          entry.comments = comment2;
          entry.description = comment2;
        }
      });
    }
    if (comment2.length === 0 || comment2[0] === '' || comment2 === '') return;
    productsForOrder.update(entries);
    updateSharedCartWithComments(
      entries,
      productFormatted.optionsGroups?.length > 0 ? createdProducts[selectedIndex] : product,
      t('common.asides.side_cart.no_comment')
    );
  };

  const { data: productPriceHistory } = useQuery({
    queryKey: ['product-price-history', productId],
    queryFn: async () => {
      const today = dayjs();
      const oneMonthBefore = today.subtract(1, 'month');

      return await getProductPriceHistory(productId, oneMonthBefore.format('YYYY-MM-DD'), today.format('YYYY-MM-DD'));
    },
    select: data => {
      if (!data.data?.length) return null;

      return Math.min(...data.data.map(p => p.price)) / 100;
    },
    enabled: !!configuration?.catalog?.product?.details?.showPriceHistory,
  });

  const selectOption = (group, index) => {
    const newOptions = optionsGroups[0].options.map((el, index2) => {
      if (index2 === index) {
        el.selected = true;
        return el;
      } else {
        el.selected = false;
        return el;
      }
    });

    setOptionsGroups(prevOptionsGroups => [
      {
        ...prevOptionsGroups[0],
        options: newOptions,
      },
      // Include the other groups if needed
    ]);

    setSelectedIndex(index);
  };

  useEffect(() => {
    let ingredients = [];
    if (ingredientDefaultValues && productInfo.ingredientsGroups?.length) {
      for (const key in ingredientDefaultValues) {
        for (const ingredient of ingredientDefaultValues[key]) ingredients.push(ingredient);
      }
    }

    const newFormattedProduct = {
      ...productInfo,
      ...transformProduct(
        productInfo,
        null,
        undefined,
        productTags,
        bundleOffersSummary,
        undefined,
        undefined,
        ingredients,
        personalizedProductsIds,
        a
      ),
      comments:
        productFormatted?.optionsGroups?.length > 0
          ? comments[0] === t('common.asides.side_cart.no_comment')
            ? ''
            : comments[0]
          : productFormatted.comments === t('common.asides.side_cart.no_comment')
          ? ''
          : productFormatted.comments,
    };
    setProductFormatted(newFormattedProduct);
    setProduct({
      id: productInfo._id,
      ...productInfo,
      ...transformProduct(
        productInfo,
        null,
        undefined,
        productTags,
        bundleOffersSummary,
        undefined,
        undefined,
        ingredients,
        personalizedProductsIds,
        a
      ), // limitations: newFormattedProduct.modifiedUom,
      comments:
        productFormatted?.optionsGroups?.length > 0
          ? comments[0] === t('common.asides.side_cart.no_comment')
            ? ''
            : comments[0]
          : productFormatted.comments === t('common.asides.side_cart.no_comment')
          ? ''
          : productFormatted.comments,
      oldPrice:
        newFormattedProduct.optionsGroups?.length > 0
          ? newFormattedProduct.optionsGroups[0].options[selectedIndex].association?.beginPricePerStepToUom ===
            newFormattedProduct.optionsGroups[0].options[selectedIndex].association?.finalPricePerStepToUom
            ? newFormattedProduct.optionsGroups[0].options[selectedIndex].association?.beginPricePerStepToUom
            : undefined
          : newFormattedProduct.finalPrice !== newFormattedProduct.beginPrice
          ? ((newFormattedProduct.beginPrice * newFormattedProduct.stepToUom) / 100).toFixed(2)
          : undefined,
    });
    if (newFormattedProduct.optionsGroups?.length > 0) {
      setOptionsGroups(newFormattedProduct?.optionsGroups ?? []);
      setSelectedOptions({});
      setCreatedProducts(initMyoProducts(newFormattedProduct, undefined));
    } else {
      setCreatedProducts([]);
      setOptionsGroups([]);
    }
    if (!productInfo?.optionsGroups?.length > 0) {
      if (extractCommentNonOption(productFormatted) === t('common.asides.side_cart.no_comment')) {
        setComments(['']);
        setTextAreaValue('');
      } else {
        setComments([extractCommentNonOption(productFormatted, ingredients)]);
        setTextAreaValue(extractCommentNonOption(productFormatted, ingredients));
      }
    }
    if (
      productInfo.bundleOfferIds &&
      productInfo.bundleOfferIds.length > 0 &&
      productInfo.bundleOfferIds.some(id => bundleOffersSummary.hasOwnProperty(id))
    ) {
      setHasBundleOffer(true);
      setBundleDescription(bundleOffersSummary[productInfo.bundleOfferIds[0]]?.description);
    }
    if (process.env.hasItemAdditionMessage) {
      setHasCartAdditionText(removeAddText(newFormattedProduct));
    }
  }, [productInfo, bundleOffersSummary, locale, ingredientDefaultValues, personalizedProductsIds]);

  const removeAddText = product => {
    const items = productsForOrder.getValue();
    const found = items.find(i => i.id === product._id);
    return !!found;
  };

  // const getToastMessage = productFormatted => {
  //   if (productFormatted?.modifiedUom > 0)
  //     return `${t('common.product_customization.have')} ${productFormatted.modifiedUom}  ${
  //       productFormatted.unitOfMeasurement
  //     } ${t('common.product_customization.in_cart')}`;
  //   else if (productFormatted.isWeighed) {
  //     return `${t('common.product_customization.have')} ${productFormatted.modifiedUom}  ${
  //       productFormatted.unitOfMeasurement
  //     } ${t('common.product_customization.in_cart')}`;
  //   } else return '';
  // };

  useEffect(() => {
    if (createdProducts?.length > 0) {
      getOptions(productFormatted?.optionsGroups);
      setComments(
        createdProducts?.map(el => (el?.comments ? el?.comments : el?.description ? el?.description : undefined))
      );
    } else {
      // if (process.env.hasItemAdditionMessage) {
      //   setHasCartAdditionText(true);
      // }
    }
  }, [createdProducts]);

  useEffect(() => {
    if (!productFormatted || productFormatted?._id !== productId) {
      setComments(['']);
      setTextAreaValue('');
      return;
    }

    handleCommentChange(
      productFormatted?.comments?.length > 0 && textAreaValue?.length === 0 ? productFormatted?.comments : textAreaValue
    );

    if (createdProducts?.length > 0) {
      setCreatedProducts(prevArray =>
        prevArray.map((item, i) => (i === selectedIndex ? { ...item, comments: textAreaValue } : item))
      );
      setComments(
        createdProducts?.map(el => (el?.comments ? el?.comments : el?.description ? el?.description : undefined))
      );
    }
    if (!product?.optionsGroups?.length > 0) {
      setComments([textAreaValue]);
    }
  }, [textAreaValue, productFormatted, productId]);

  useEffect(() => {
    if (productInfo?._id) {
      viewItem(getProductInfo(productInfo));
      setToastMessage('');
      setSlideIndex(0);

      setTimeout(() => {
        setSlideIndex(undefined);
      }, 10);
    }
  }, [productInfo?._id]);

  useEffect(() => {
    const userListSubscription = UserListsStore.subscribe(setLists);
    const productsForOrder$ = productsForOrder.subscribe(setProductsOrder);
    return () => {
      userListSubscription.unsubscribe();
      productsForOrder$.unsubscribe();
    };
  }, []);

  const listStatus = useMemo(() => {
    return productFormatted && lists ? checkListStatus(productFormatted) : 'noList';
  }, [productFormatted, lists]);

  useEffect(() => {
    let ingredients = [];
    if (ingredientDefaultValues) {
      for (const key in ingredientDefaultValues) {
        for (const ingredient of ingredientDefaultValues[key]) ingredients.push(ingredient);
      }
    }
    const latestProduct = productsOrder?.find(prod => prod.id === productId);
    const newFormattedProduct = {
      ...productInfo,
      ...transformProduct(
        productInfo,
        null,
        undefined,
        productTags,
        bundleOffersSummary,
        undefined,
        undefined,
        ingredients,
        personalizedProductsIds,
        a
      ),
      comments:
        productFormatted?.optionsGroups?.length > 0
          ? comments[0] === t('common.asides.side_cart.no_comment')
            ? ''
            : comments[0]
          : productFormatted.comments === t('common.asides.side_cart.no_comment')
          ? ''
          : latestProduct?.comments
          ? latestProduct?.comments
          : '',
    };
    setProductFormatted(newFormattedProduct);
    setProduct({
      id: productInfo._id,
      ...productInfo,
      ...transformProduct(
        productInfo,
        null,
        undefined,
        productTags,
        bundleOffersSummary,
        undefined,
        undefined,
        ingredients,
        personalizedProductsIds,
        a
      ),
      comments:
        productFormatted?.optionsGroups?.length > 0
          ? comments[0] === t('common.asides.side_cart.no_comment')
            ? ''
            : comments[0]
          : productFormatted.comments === t('common.asides.side_cart.no_comment')
          ? ''
          : productFormatted.comments,
      oldPrice:
        newFormattedProduct.optionsGroups?.length > 0
          ? newFormattedProduct.optionsGroups[0].options[selectedIndex].association?.beginPricePerStepToUom ===
            newFormattedProduct.optionsGroups[0].options[selectedIndex].association?.finalPricePerStepToUom
            ? newFormattedProduct.optionsGroups[0].options[selectedIndex].association?.beginPricePerStepToUom
            : undefined
          : newFormattedProduct.finalPrice !== newFormattedProduct.beginPrice
          ? ((newFormattedProduct.beginPrice * newFormattedProduct.stepToUom) / 100).toFixed(2)
          : undefined,
    });
    if (newFormattedProduct.optionsGroups?.length > 0) {
      // setOptionsGroups(newFormattedProduct?.optionsGroups ?? []);
      // setSelectedOptions({});
      setCreatedProducts(initMyoProducts(newFormattedProduct, undefined));
    } else {
      setCreatedProducts([]);
      setOptionsGroups([]);
    }
    if (!productInfo?.optionsGroups?.length > 0) {
      if (extractCommentNonOption(newFormattedProduct) === t('common.asides.side_cart.no_comment')) {
        setComments(['']);
        setTextAreaValue('');
      } else {
        setComments([extractCommentNonOption(newFormattedProduct, ingredients)]);
        setTextAreaValue(extractCommentNonOption(newFormattedProduct, ingredients));
      }
    }
    if (
      productInfo.bundleOfferIds &&
      productInfo.bundleOfferIds.length > 0 &&
      productInfo.bundleOfferIds.some(id => bundleOffersSummary.hasOwnProperty(id)) &&
      bundleOffersSummary
    ) {
      setHasBundleOffer(true);
      setBundleDescription(bundleOffersSummary[productInfo.bundleOfferIds[0]]?.description);
    }
  }, [productsOrder, ingredientDefaultValues, personalizedProductsIds, productId]);

  if (!productInfo) return null;
  const breadCrumbItems = selectedCategory?.parents
    ? [
        {
          title: selectedCategory.parents[0].name,
          link: `/${locale}/categories/${selectedCategory.parents[0].slug}`,
        },
        { title: selectedCategory.name, link: `/${locale}/${selectedCategory.fullSlug}` },
        { title: productFormatted.displayName, link: productInfo.slug },
      ]
    : [
        { title: selectedCategory.name, link: `/${locale}/${selectedCategory.fullSlug}` },
        { title: productFormatted.displayName, link: productInfo.slug },
      ];

  const images = getProductAlternativeImages(productFormatted);
  const generateIngredientsErrorMessage = (key, options) => {
    return t(`common.ingredients.modal.errors.${key}`, options);
  };
  // const imagesWithPrimary = addPrimaryImage(productFormatted, images);

  const shouldDisplayNutritionFacts = productDetails?.nutritionItems?.columns?.titles?.length > 0;
  const shouldDisplayProductContentGroups = productDetails?.contentGroups && productDetails?.contentGroups.length;
  return (
    <>
      <Head>
        <title>
          {process.env.appName} | {productFormatted.name}
        </title>
        <meta name="description" content={productDetails?.description || `Αpp for ${process.env.appName} `} />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta property="og:title" content={`${process.env.appName} | ${productFormatted.name}`} />
        <meta property="og:description" content={productDetails?.description || ''} />
        <link rel="icon" href={responsiveLogo} />
      </Head>

      <main className={styles.mainContainer} data-product-id={productFormatted?.sku}>
        {isMobile ? (
          <div className={styles.breadCrumbContainer}>
            <WaveBreadCrumb
              LinkComponent={Link}
              items={breadCrumbItems}
              iconLink={`/${locale === defaultLocale ? '' : locale}`}
              isRtl={isRtl}
            />
          </div>
        ) : null}

        <div className={productStyles.productsWrapper}>
          {isMobile ? null : (
            <div className={productStyles.breadcrumb}>
              <WaveBreadCrumb
                LinkComponent={Link}
                items={breadCrumbItems}
                iconLink={`/${locale === defaultLocale ? '' : locale}`}
                isRtl={isRtl}
              />
            </div>
          )}

          <div className={productStyles.productColumns}>
            <div className={productStyles.productColumn}>
              <ProductDetailsImageSection
                images={images}
                hasOffer={productFormatted.isOnOffer}
                percentage={productFormatted.offerValue}
                shape={process.env.offerShape}
                offerShape={process.env.offerShape}
                size={0}
                hasIcon={process.env.offerHasIcon}
                hasSticker={process.env.offerHasSticker || false}
                hasRoundedCorners={process.env.hasRoundedCorners}
                defaultProductImage={defaultProdImg}
                productName={productFormatted?.name}
                imageWording={t('product_details.image')}
                thumbnailWording={t('product_details.thumbnail')}
                isRtl={isRtl}
                lightBoxAriaLabels={lightBoxAriaLabels}
              />

              {isMobile ? (
                <div className={productStyles.productDetails}>
                  <ProductCustomization
                    availabilityDays={
                      config?.timeslots?.calculation?.strategy === 'USING_CART_INFO'
                        ? product?.availabilityDays
                        : undefined
                    }
                    availabilityText={
                      config?.timeslots?.calculation?.strategy === 'USING_CART_INFO'
                        ? product?.availability?.type === 'CONSTRAINED'
                          ? describeDeliveryRule(product?.availability?.constrained, undefined, a, true)
                          : product?.availability?.type === 'EXACT'
                          ? createAvailabilityTextDetails(product?.availabilityDays, a, product?.availabilityTimeUnit)
                          : product?.availability?.type === 'TIMEFRAME'
                          ? createTimeFrameAvailabilityTextDetails(product?.availability?.timeframe, a, true)
                          : null
                        : null
                    }
                    qsLoading={productInfoIsLoading}
                    getErrorMessage={generateIngredientsErrorMessage}
                    ingredientsAddAsNewLabel={t('common.ingredients.add_as_new')}
                    ingredientsUpdateCurrentBtnLabel={t('common.ingredients.update_current')}
                    currencyFormatter={formatter}
                    textAreaDescription={t('product_details.product_customization.comment_placeholder')}
                    qsUnavailableText={t('common.qs.default_unavailable')}
                    qsText={t('common.qs.default')}
                    textAreaTitle={t('product_details.product_customization.comment_label')}
                    product={createdProducts.length > 0 ? createdProducts[selectedIndex] : product}
                    codeTitle={t('product_details.product_customization.code_title')}
                    onTextAreaChange={setTextAreaValue}
                    textAreaValue={textAreaValue}
                    ingredientDefaultValues={isEmpty(ingredientDefaultValues) ? undefined : ingredientDefaultValues}
                    onAddToCart={(data, quantity) => {
                      const selectedQuantity = quantity || 1;
                      if (product.ingredientsGroups?.length) {
                        handleProductChange(product, 'add', data, selectedQuantity);
                        push(
                          {
                            pathname: asPath.split('?')[0],
                            search: flattenIngredientsArray(data).length
                              ? `?preSelectedIngredients=${JSON.stringify(flattenIngredientsArray(data))}`
                              : '',
                          },
                          undefined,
                          {
                            shallow: true,
                          }
                        );
                        return;
                      }
                      handleProductChange(product, 'add', null, selectedQuantity);
                    }}
                    onRemoveFromCart={(data, quantity) => {
                      const selectedQuantity = quantity || 1;
                      if (product.ingredientsGroups?.length) {
                        handleProductChange(product, 'remove', data);
                        return;
                      }
                      handleProductChange(product, 'remove', null, selectedQuantity);
                    }}
                    setMaxSnackBar={setShowSnackBar}
                    offerBannerState="notStarted"
                    offerBannerEmoji="🙌"
                    offerBannerText={bundleDescription}
                    offerBannerInDetails={true}
                    offerBannerButtonText={t('product_details.product_customization.banner_text')}
                    offerBannerOnButtonPress={() => {
                      SelectedBundleOfferStore.update(product);
                      BundleOfferSideSheetStore.update(true);
                    }}
                    hasOfferBanner={hasBundleOffer}
                    tabOptionsList={optionsGroups[0]?.options ?? []}
                    tabOptionsListTitle={
                      optionsGroups[0]?.title ?? t('product_details.product_customization.tab_list_title')
                    }
                    onChipsPress={selectOption}
                    tabOptionsListSubTitle={t('product_details.product_customization.tab_list_subtitle')}
                    selectedIndex={selectedIndex}
                    comments={comments}
                    formattedProductOfferDuration={formattedProductOfferDuration}
                    toastMessage={toastMessage}
                    listClick={item => openListSideSheet(item)}
                    status={listStatus}
                    listIcon={process.env.listIcon}
                    offerLabelHasRibbon={process.env.offerLabelHasRibbon}
                    showBarcode={showBarcode}
                    commentMaxLength={commentMaxLength}
                    backButtonText={t('product_details.product_customization.back_menu')}
                    onBackButtonPress={back}
                    cartAddedText={t('product_details.product_customization.in_cart')}
                    hasCartAdditionText={hasCartAdditionText}
                    lowestPriceText={
                      productPriceHistory && shouldDisplayPriceHistory
                        ? t('product_details.product_customization.lowest_price_history', {
                            price: formatter.format(productPriceHistory),
                          })
                        : null
                    }
                    forYouLabel={t('common.user.profile.menu.for_you')}
                    allBrandFirstText={t('product_details.brand.first_text')}
                    allBrandSecondText={t('product_details.brand.second_text')}
                    // the id is added because there is no other way to get the brand
                    brandPageLink={`/brand/${encodeURIComponent(productInfo?.brand)}`}
                    BrandLinkComponent={props => {
                      return <Link {...props} />;
                    }}
                    isBrandPageEnabled={isBrandPageEnabled}
                    isPackagingFeeEnabled={config?.catalog?.product?.packagingFee?.enabled}
                    packagedProduct={
                      config?.catalog?.product?.packagingFee?.enabled
                        ? { name: packagedProduct?.name, finalPrice: packagedProduct?.finalPrice }
                        : null
                    }
                    packagingFeeIconType={process.env.packagingFeeIconType}
                    maxQuantityForDropdown={config?.catalog?.product?.quantitySelector?.maxQuantityForDropdown?.value}
                    dropdownEnabled={true}
                  />
                </div>
              ) : null}
              <div className={cn(productStyles.productDetails, productStyles.productDescription)}>
                <ProductInfo
                  descriptionTitle={
                    productDetails?.description
                      ? t('product_details.product_info.default_description_title')
                      : undefined
                  }
                  description={productDetails?.description}
                  isCollapsible
                />
                {productDetails?.videos &&
                  productDetails?.videos.length > 0 &&
                  productDetails?.videos.map(video => (
                    <ProductVideo
                      key={video.index}
                      // TODO: Add proper video description
                      // description={productDetails?.description}
                      videoUrl={video.url}
                      descriptionTitle={t('product_details.product_video.default_description_title')}
                      isCollapsible
                    />
                  ))}

                {shouldDisplayNutritionFacts ? (
                  <div className={productStyles.productDetailsNoMargin}>
                    <NutritionFacts
                      isCollapsible
                      nutritionItems={productDetails?.nutritionItems}
                      moreText={t('common.nutrition_facts.see_more')}
                      lessText={t('common.nutrition_facts.see_less')}
                    />
                  </div>
                ) : null}
                {/* {shouldDisplayAdditionalInfo ? (
                  <AdditionalInfo
                    instructions={productDetails?.instructions}
                    instructionsTitle={productDetails?.instructions ? 'Οδηγίες Χρήσης / Αποθήκευσης' : undefined}
                    madeIn={productDetails?.madeIn}
                    madeInTitle={productDetails?.madeIn ? 'Χώρα Παραγωγής' : undefined}
                    supplierInfo={productDetails?.supplierInfo}
                    supplierInfoTitle={productDetails?.supplierInfo ? 'Πληροφορίες προμηθευτή' : undefined}
                    smallDescription={productDetails?.smallDescription}
                    smallDescriptionTitle={productDetails?.smallDescription ? 'Μικρή περιγραφή' : undefined}
                  />
                ) : null}*/}

                {shouldDisplayProductContentGroups
                  ? productDetails?.contentGroups
                      ?.sort?.((a, b) => a.index - b.index)
                      ?.map?.((cg, index) => (
                        <ProductContentGroup key={index} index={cg.index} items={cg.items} title={cg.title} />
                      ))
                  : null}
              </div>
            </div>

            {isMobile ? null : (
              <div className={productStyles.productInfo}>
                <ProductCustomization
                  availabilityDays={
                    config?.timeslots?.calculation?.strategy === 'USING_CART_INFO'
                      ? product?.availabilityDays
                      : undefined
                  }
                  availabilityText={
                    config?.timeslots?.calculation?.strategy === 'USING_CART_INFO'
                      ? product?.availability?.type === 'CONSTRAINED'
                        ? describeDeliveryRule(product?.availability?.constrained, undefined, a, true)
                        : product?.availability?.type === 'EXACT'
                        ? createAvailabilityTextDetails(product?.availabilityDays, a, product?.availabilityTimeUnit)
                        : product?.availability?.type === 'TIMEFRAME'
                        ? createTimeFrameAvailabilityTextDetails(product?.availability?.timeframe, a, true)
                        : null
                      : null
                  }
                  qsLoading={productInfoIsLoading}
                  getErrorMessage={generateIngredientsErrorMessage}
                  ingredientsAddAsNewLabel={t('common.ingredients.add_as_new')}
                  ingredientsUpdateCurrentBtnLabel={t('common.ingredients.update_current')}
                  currencyFormatter={formatter}
                  textAreaDescription={t('product_details.product_customization.comment_placeholder')}
                  qsUnavailableText={t('common.qs.default_unavailable')}
                  qsText={t('common.qs.default')}
                  textAreaTitle={t('product_details.product_customization.comment_label')}
                  product={createdProducts.length > 0 ? createdProducts[selectedIndex] : product}
                  codeTitle={t('product_details.product_customization.code_title')}
                  onTextAreaChange={setTextAreaValue}
                  textAreaValue={textAreaValue}
                  ingredientDefaultValues={isEmpty(ingredientDefaultValues) ? undefined : ingredientDefaultValues}
                  onAddToCart={(data, quantity) => {
                    const selectedQuantity = quantity || 1;
                    if (product.ingredientsGroups?.length) {
                      handleProductChange(product, 'add', data, selectedQuantity);
                      push(
                        {
                          pathname: asPath.split('?')[0],
                          search: flattenIngredientsArray(data).length
                            ? `?preSelectedIngredients=${JSON.stringify(flattenIngredientsArray(data))}`
                            : '',
                        },
                        undefined,
                        {
                          shallow: true,
                        }
                      );
                      return;
                    }
                    handleProductChange(product, 'add', null, selectedQuantity);
                  }}
                  onRemoveFromCart={(data, quantity) => {
                    const selectedQuantity = quantity || 1;
                    if (product.ingredientsGroups?.length) {
                      handleProductChange(product, 'remove', data);
                      return;
                    }
                    handleProductChange(product, 'remove', null, selectedQuantity);
                  }}
                  onIngredientUpdateCurrent={product.ingredientsGroups?.length ? ingredientsUpdateCurrent : undefined}
                  onIngredientsAddAsNew={product.ingredientsGroups?.length ? ingredientsAddAsNew : undefined}
                  offerBannerState="notStarted"
                  offerBannerEmoji="🙌"
                  offerBannerText={bundleDescription}
                  offerBannerInDetails={true}
                  setMaxSnackBar={setShowSnackBar}
                  offerBannerButtonText={t('product_details.product_customization.banner_text')}
                  offerBannerOnButtonPress={() => {
                    SelectedBundleOfferStore.update(product);
                    BundleOfferSideSheetStore.update(true);
                  }}
                  hasOfferBanner={hasBundleOffer}
                  tabOptionsList={optionsGroups[0]?.options ?? []}
                  tabOptionsListTitle={
                    optionsGroups[0]?.title ?? t('product_details.product_customization.tab_list_title')
                  }
                  onChipsPress={selectOption}
                  tabOptionsListSubTitle={t('product_details.product_customization.tab_list_subtitle')}
                  selectedIndex={selectedIndex}
                  comments={comments}
                  toastMessage={toastMessage}
                  listClick={item => {
                    openListSideSheet(item);
                  }}
                  status={listStatus}
                  listIcon={process.env.listIcon}
                  offerLabelHasRibbon={process.env.offerLabelHasRibbon}
                  showBarcode={showBarcode}
                  commentMaxLength={commentMaxLength}
                  backButtonText={t('product_details.product_customization.back_menu')}
                  onBackButtonPress={back}
                  cartAddedText={t('product_details.product_customization.in_cart')}
                  hasCartAdditionText={hasCartAdditionText}
                  lowestPriceText={
                    shouldDisplayPriceHistory && productPriceHistory
                      ? t('product_details.product_customization.lowest_price_history', {
                          price: formatter.format(productPriceHistory),
                        })
                      : null
                  }
                  forYouLabel={t('common.user.profile.menu.for_you')}
                  allBrandFirstText={t('product_details.brand.first_text')}
                  allBrandSecondText={locale !== 'el' ? t('product_details.brand.second_text') : ''}
                  // the id is added because there is no other way to get the brand
                  brandPageLink={`/brand/${encodeURIComponent(productInfo?.brand)}`}
                  BrandLinkComponent={props => {
                    return <Link {...props} />;
                  }}
                  isBrandPageEnabled={isBrandPageEnabled}
                  packagedProduct={
                    config?.catalog?.product?.packagingFee?.enabled
                      ? { name: packagedProduct?.name, finalPrice: packagedProduct?.finalPrice }
                      : null
                  }
                  isPackagingFeeEnabled={config?.catalog?.product?.packagingFee?.enabled}
                  packagingFeeIconType={process.env.packagingFeeIconType}
                  formattedProductOfferDuration={formattedProductOfferDuration}
                  maxQuantityForDropdown={config?.catalog?.product?.quantitySelector?.maxQuantityForDropdown}
                  dropdownEnabled={true}
                />
              </div>
            )}
          </div>

          {frequent && (
            <section className={productStyles.bottomAisleContainer}>
              <ProductAisleV2
                productId={productFormatted._id}
                analyticsFunction={(products, list) => {
                  aislesViewAnalyticsFunction(products, list, sessionId, productInfo.sku);
                }}
                list={'bought_together'}
                title={t('product_details.frequently_bought_together.title')}
                classOption={productStyles.bottomAisle}
                products={frequent}
                hasPadding={true}
                isMobile={isMobile}
                showMore={false}
                onListClick={openListSideSheet}
                setMaxSnackBar={setShowSnackBar}
                addProduct={product =>
                  addProduct(
                    product,
                    true,
                    {
                      item_list_id: `bought_together`,
                      item_list_name: `Bought Together`,
                      session_id: sessionId,
                    },
                    false,
                    t('common.asides.side_cart.no_comment')
                  )
                }
                removeProduct={product =>
                  removeProduct(
                    product,
                    {
                      item_list_id: `bought_together`,
                      item_list_name: `Bought Together`,
                      session_id: sessionId,
                    },
                    t('common.asides.side_cart.no_comment')
                  )
                }
                tilesSize="default"
                horizontal
                LinkComponent={props => {
                  const propsWithCleanUrl = getProductPropsWithCleanUrl(props);

                  return (
                    <Link
                      prefetch={false}
                      {...propsWithCleanUrl}
                      onClick={() => {
                        const product = getProductBasedOnSlug(frequent, propsWithCleanUrl.href);

                        if (product) {
                          ListStore.update(`bought_together`, product._id);
                          selectItem(getProductInfo(product), {
                            item_list_id: `bought_together`,
                            item_list_name: `Bought Together`,
                            index: getProductIndexBasedOnSlug(propsWithCleanUrl.href) + 1,
                          });
                        }
                      }}
                    />
                  );
                }}
                defaultQsText={t('common.qs.default')}
                defaultQsUnavailableText={t('common.qs.default_unavailable')}
                ImageComponent={CustomImage}
                slideToMoveTo={slideIndex}
                offerLabelHasRibbon={process.env.offerLabelHasRibbon}
                isTileDetailsAnimated={process.env.isTileDetailsAnimated}
                ctaIcon={process.env.ctaIcon}
                listIcon={process.env.listIcon}
                currencyFormatter={formatter}
                hasRoundedCorners={process.env.hasRoundedCorners}
                defaultProductImage={defaultProdImg}
                forYouLabel={t('common.user.profile.menu.for_you')}
                isRtl={isRtl}
              />
            </section>
          )}

          {similar && (
            <section className={productStyles.bottomAisleContainer}>
              <ProductAisleV2
                productId={productFormatted._id}
                analyticsFunction={(products, list) =>
                  aislesViewAnalyticsFunction(products, list, sessionId, productInfo.sku)
                }
                list={'similar'}
                title={t('product_details.similar_products.title')}
                classOption={productStyles.bottomAisle}
                products={similar}
                hasPadding={true}
                isMobile={isMobile}
                showMore={false}
                onListClick={openListSideSheet}
                setMaxSnackBar={setShowSnackBar}
                addProduct={product =>
                  addProduct(
                    product,
                    true,
                    {
                      item_list_id: `similar`,
                      item_list_name: `Similar`,
                      session_id: sessionId,
                    },
                    false,
                    t('common.asides.side_cart.no_comment')
                  )
                }
                removeProduct={product =>
                  removeProduct(
                    product,
                    {
                      item_list_id: `similar`,
                      item_list_name: `Similar`,
                      session_id: sessionId,
                    },
                    t('common.asides.side_cart.no_comment')
                  )
                }
                tilesSize="default"
                horizontal
                LinkComponent={props => {
                  const propsWithCleanUrl = getProductPropsWithCleanUrl(props);

                  return (
                    <Link
                      prefetch={false}
                      {...propsWithCleanUrl}
                      onClick={() => {
                        const product = getProductBasedOnSlug(similar, propsWithCleanUrl.href);

                        if (product) {
                          ListStore.update(`similar`, product._id);
                          selectItem(getProductInfo(product), {
                            item_list_id: `similar`,
                            item_list_name: `Similar`,
                            index: getProductIndexBasedOnSlug(propsWithCleanUrl.href) + 1,
                          });
                        }
                      }}
                    />
                  );
                }}
                defaultQsText={t('common.qs.default')}
                defaultQsUnavailableText={t('common.qs.default_unavailable')}
                ImageComponent={CustomImage}
                slideToMoveTo={slideIndex}
                offerLabelHasRibbon={process.env.offerLabelHasRibbon}
                isTileDetailsAnimated={process.env.isTileDetailsAnimated}
                ctaIcon={process.env.ctaIcon}
                listIcon={process.env.listIcon}
                currencyFormatter={formatter}
                hasRoundedCorners={process.env.hasRoundedCorners}
                defaultProductImage={defaultProdImg}
                forYouLabel={t('common.user.profile.menu.for_you')}
                isRtl={isRtl}
              />
            </section>
          )}

          {brandProducts && isBrandPageEnabled && (
            <section className={productStyles.bottomAisleContainer}>
              <ProductAisleV2
                productId={productFormatted._id}
                list={'brand_products_aisle'}
                analyticsFunction={(products, list) => aislesViewAnalyticsFunction(products, list)}
                title={titleCaseString(productInfo?.brand) + ' ' + t('common.brand.products')}
                classOption={productStyles.bottomAisle}
                products={brandProducts}
                hasPadding={true}
                isMobile={isMobile}
                showMore={false}
                onListClick={openListSideSheet}
                setMaxSnackBar={setShowSnackBar}
                addProduct={product =>
                  addProduct(
                    product,
                    true,
                    {
                      item_list_id: 'brandProducts',
                      item_list_name: 'brandProducts',
                    },
                    false,
                    t('common.asides.side_cart.no_comment')
                  )
                }
                removeProduct={product =>
                  removeProduct(
                    product,
                    {
                      item_list_id: 'brandProducts',
                      item_list_name: 'brandProducts',
                    },
                    t('common.asides.side_cart.no_comment')
                  )
                }
                tilesSize="default"
                horizontal
                LinkComponent={props => {
                  const propsWithCleanUrl = getProductPropsWithCleanUrl(props);

                  return (
                    <Link
                      prefetch={false}
                      {...propsWithCleanUrl}
                      onClick={() => {
                        const product = getProductBasedOnSlug(similar, propsWithCleanUrl.href);

                        if (product) {
                          ListStore.update(`brandProducts`, product._id);
                          selectItem(getProductInfo(product), {
                            item_list_id: `brand_products_aisle`,
                            item_list_name: `brand_products_aisle`,
                            index: getProductIndexBasedOnSlug(propsWithCleanUrl.href) + 1,
                          });
                        }
                      }}
                    />
                  );
                }}
                defaultQsText={t('common.qs.default')}
                defaultQsUnavailableText={t('common.qs.default_unavailable')}
                ImageComponent={CustomImage}
                slideToMoveTo={slideIndex}
                offerLabelHasRibbon={process.env.offerLabelHasRibbon}
                isTileDetailsAnimated={process.env.isTileDetailsAnimated}
                ctaIcon={process.env.ctaIcon}
                listIcon={process.env.listIcon}
                currencyFormatter={formatter}
                defaultProductImage={defaultProdImg}
                linkText={t('common.brand.all_link')}
                productsLink={`${locale !== defaultLocale ? `/${locale}` : ''}/brand/${encodeURIComponent(
                  productInfo?.brand
                )}`}
                keepAllLink={true}
                forYouLabel={t('common.user.profile.menu.for_you')}
                isRtl={isRtl}
              />
            </section>
          )}
          <SnackBar open={showSnackBar} setOpen={setShowSnackBar} text={t('common.snackbar')} position={'center'} />
        </div>
      </main>
    </>
  );
}

Products.getLayout = function getLayout(page) {
  return <PageLayout>{page}</PageLayout>;
};

export async function getStaticPaths() {
  return { paths: [], fallback: 'blocking' };
}
//paths.concat(manualPaths);
// }

export async function getStaticProps(context) {
  const productNameWithId = context.params.slug[context.params.slug.length - 1];
  const productId = productNameWithId.slice(productNameWithId.lastIndexOf('-') + 1, productNameWithId.length);

  // Validate that productId actually exists and is of type ObjectId https://www.mongodb.com/docs/manual/reference/method/ObjectId/
  const objectIdRegex = /^[a-fA-F0-9]{24}$/;
  if (!objectIdRegex.test(productId)) {
    return {
      notFound: true,
    };
  }

  const layoutProps = await getLayoutProps({ headerProps: { selectedIndex: 0 } }, context.locale);
  let productDetails, productInfo, similarProducts, frequentlyBoughtTogether, brandProductsApiRes;

  try {
    productInfo = await getProductInfoV3(productId, context.locale, process.env.PRIVATE_API_KEY);
  } catch (err) {
    productInfo = null;
    console.log('info error', err);
  }

  try {
    productDetails = await getProductDetailsV3(productId, context.locale, process.env.PRIVATE_API_KEY);
  } catch (err) {
    productDetails = null;
    console.log('details error', err);
  }

  if (!productInfo) {
    return {
      notFound: true,
    };
  }

  try {
    const res = await getFrequentlyBoughtTogether(productId, context.locale);
    frequentlyBoughtTogether = res?.data?.map?.(enhanceProductWithSlug);
  } catch (err) {
    frequentlyBoughtTogether = null;
    console.log('frequently bought together error', err);
  }

  try {
    const res = await getSimilarProducts(productId, context.locale);
    similarProducts = res?.data?.map?.(enhanceProductWithSlug);
  } catch (err) {
    similarProducts = null;
    console.log('similar products error', err);
  }

  //if brand is undefined, we don't want to show the brand products
  try {
    if (productInfo?.brand) {
      const res = await getBrandProducts(productInfo?.brand, context.locale);
      brandProductsApiRes = res?.map?.(enhanceProductWithSlug);
    } else {
      brandProductsApiRes = null;
    }
  } catch (err) {
    brandProductsApiRes = null;
    console.log('brand products error', err);
  }

  const messages = {
    common: (await import(`../../../prebuild-data/i18n/${context.locale}/common.json`)).default,
    product_details: (await import(`../../../prebuild-data/i18n/${context.locale}/product_details.json`)).default,
  };

  const slugArrayClone = [...context.params.slug];
  slugArrayClone.pop();

  if (productDetails.description) productDetails.description = sanitizeHtml(productDetails.description);

  return {
    props: {
      ...layoutProps,
      selectedCategory: getCategoryDetailsFromSlug(slugArrayClone.join('/'), context.locale) || null,
      productInfoInitial: { ...productInfo, _id: productId } || null,
      productDetails: productDetails || null,
      frequentlyBoughtTogether,
      similarProducts,
      brandProductsApiRes,
      messages,
      productId,
    },
    revalidate: 1800,
  };
}
